import { processTaskButtonLabel, processTaskLink, splitTaskTitle, type CaseDto, type CaseProcessDto, type MeDto, type ProcessTaskDto } from '@bqcrm/shared';
import { Anchor, Badge, Button, Card, Group, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api } from '../../api';
import { ClaimTaskButton } from '../../components/process/ProcessTasks';
import { NextAction } from '../CasesPage';

/** Ile zadań pokazujemy w pasku; reszta — link do zakładki „Zadania”. */
const MAX_ROWS = 3;

/**
 * Otwarte zadania sprawy, które ta osoba może wykonać: jej własne (także w zastępstwie) i te do przejęcia z jej uprawnieniem
 * (jak podpowiedź „Co teraz?” z serwera). Ten sam klucz co zakładka „Zadania” — wspólny cache.
 */
export function useNowTasks(caseId: string, me: MeDto): ProcessTaskDto[] {
  const q = useQuery({ queryKey: ['case-process', caseId], queryFn: () => api<CaseProcessDto>(`/cases/${caseId}/process`) });
  const due = (t: ProcessTaskDto) => (t.dueAt ? Date.parse(t.dueAt) : Infinity);
  return (q.data?.tasks ?? [])
    .filter((t) => t.status === 'otwarte' && (t.assigneeId === me.id || !!t.forUserName || t.can.claim))
    .sort((a, b) => Number(b.overdue) - Number(a.overdue) || due(a) - due(b));
}

const border = (tone: string) => `4px solid var(--mantine-color-${tone === 'late' ? 'red' : tone === 'todo' ? 'eko' : 'gray'}-6)`;

/**
 * Karta „Co teraz?” u góry sprawy. Jedno zadanie (albo zwykła podpowiedź) — jedna linia z przyciskiem;
 * kilka zadań — każde w swoim wierszu: krótki tytuł, szczegóły drobniej, „Przejmij” (gdy do przejęcia) i przycisk do miejsca wykonania.
 */
export function NowCard({ c, tasks, nextHref }: { c: CaseDto; tasks: ProcessTaskDto[]; nextHref: string | null }) {
  if (tasks.length >= 2) {
    const shown = tasks.slice(0, MAX_ROWS);
    return (
      <Card mb="lg" padding="sm" style={{ borderLeft: border(tasks.some((t) => t.overdue) ? 'late' : 'todo') }}>
        <Text size="xs" fw={700} tt="uppercase" c="dimmed">
          Co teraz? · {tasks.length} {tasks.length < 5 ? 'zadania' : 'zadań'}
        </Text>
        {shown.map((t, i) => {
          const { head, detail } = splitTaskTitle(t.title);
          return (
            <Group key={t.id} justify="space-between" align="center" wrap="wrap" gap="xs" py={6} style={i ? { borderTop: '1px solid var(--mantine-color-default-border)' } : undefined}>
              <div style={{ flex: '1 1 280px', minWidth: 0 }}>
                <Group gap={6} wrap="wrap">
                  <Text size="sm" fw={600}>
                    {head}
                  </Text>
                  {!t.assigneeId && (
                    <Badge size="xs" variant="light" color="orange">
                      do przejęcia
                    </Badge>
                  )}
                  {t.forUserName && (
                    <Badge size="xs" variant="light" color="gray">
                      za: {t.forUserName}
                    </Badge>
                  )}
                  {t.overdue && (
                    <Badge size="xs" variant="light" color="red">
                      po terminie
                    </Badge>
                  )}
                </Group>
                {detail && (
                  <Text size="xs" c="dimmed">
                    {detail}
                  </Text>
                )}
              </div>
              <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
                <ClaimTaskButton t={t} />
                <Button component={Link} to={processTaskLink(t)} size="xs" variant={i === 0 ? 'filled' : 'light'}>
                  {processTaskButtonLabel(t)}
                </Button>
              </Group>
            </Group>
          );
        })}
        {tasks.length > MAX_ROWS && (
          <Anchor component={Link} to={`/sprawy/${c.id}?zakladka=zadania`} size="xs">
            +{tasks.length - MAX_ROWS} więcej — zadania sprawy ›
          </Anchor>
        )}
      </Card>
    );
  }
  const next = c.next;
  if (!next) return null;
  return (
    <Card mb="lg" padding="sm" style={{ borderLeft: border(next.tone) }}>
      <Group gap="sm" wrap="nowrap">
        <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ flexShrink: 0 }}>
          Co teraz?
        </Text>
        <div style={{ flex: 1, minWidth: 0 }}>
          <NextAction next={next} c={c} />
        </div>
        {nextHref && next.action && (
          <Button component={Link} to={nextHref} size="xs" variant={next.tone === 'wait' ? 'light' : 'filled'} style={{ flexShrink: 0 }}>
            {next.action.label}
          </Button>
        )}
      </Group>
    </Card>
  );
}
